<template>
	<v-skeleton-loader v-if="loading" v-bind="loader" width="100%" type="card"></v-skeleton-loader>
	<v-form
		v-else
		class="profile-form"
		ref="form"
		v-model="valid"
		lazy-validation
		@submit.prevent="validateAndSubmit"
	>
		<v-card flat class="elevation-0">
			<v-row>
				<v-col cols="12">
					<div>
						<div class="d-flex pl-0 justify-space-between align-center">
							<v-card-title class="pl-0 d-flex profile-form__title"
								>Name</v-card-title
							>
							<v-card-actions>
								<v-btn
									class="d-flex rounded-pill text-capitalize profile-form__button"
									>Edit</v-btn
								>
							</v-card-actions>
						</div>
						<v-text-field
							class="pt-0"
							placeholder="Enter your Name"
							v-model="fields.name"
							:rules="rules.name"
							:value="fields.name"
							:error-messages="field_errors.name"
							requred
						>
						</v-text-field>
					</div>
					<v-divider class="profile-form__divider"></v-divider>
				</v-col>
			</v-row>
			<v-row>
				<v-col cols="12">
					<div>
						<div class="d-flex pl-0 justify-space-between align-center">
							<v-card-title class="pl-0 d-flex profile-form__title">Bio</v-card-title>
							<v-card-actions>
								<v-btn
									class="d-flex rounded-pill text-capitalize profile-form__button"
									>Edit</v-btn
								>
							</v-card-actions>
						</div>
						<v-text-field
							class="pt-0"
							placeholder="Add your bio"
							v-model="fields.bio"
							:value="fields.bio"
							:error-messages="field_errors.bio"
							full-width
						>
						</v-text-field>
					</div>
					<v-divider class="profile-form__divider"></v-divider>
				</v-col>
			</v-row>
			<v-row>
				<v-col cols="12">
					<div>
						<div class="d-flex pl-0 justify-space-between align-center">
							<v-card-title class="pl-0 d-flex profile-form__title"
								>Photo</v-card-title
							>
							<v-card-actions>
								<v-btn
									class="d-flex rounded-pill text-capitalize profile-form__button"
									>Edit</v-btn
								>
							</v-card-actions>
						</div>

						<div class="d-flex">
							<v-card-text class="pl-0 profile-form__description mr-10">
								<p>
									Your photo appears on your Profile page and with your stories
									across.
								</p>
								<p>
									Recommended size: Square, at least 1000 pixels per side. File
									type: JPG, PNG or GIF.
								</p>
							</v-card-text>
							<v-card-text class="ml-15">
								<div class="d-flex flex-column" style="max-width: 70px">
									<v-icon
										class="justify-end image-close-button"
										size="12px"
										@click="removeAvatar"
										tile
										>mdi-close
									</v-icon>
									<image-input class="profile-form__avatar" v-model="avatar">
										<div slot="activator">
											<v-avatar
												size="70px"
												v-ripple
												v-if="!avatarSrc"
												class="profile-form__image-input grey lighten-3 mb-3"
											>
												<span>Upload</span>
											</v-avatar>
											<v-avatar
												size="70px"
												v-else
												class="profile-form__image-input mb-3"
											>
												<img
													referrerpolicy="no-referrer"
													:src="avatarSrc"
													alt="avatar"
												/>
											</v-avatar>
										</div>
									</image-input>
								</div>
							</v-card-text>
						</div>
					</div>
					<v-btn
						class="d-flex rounded-pill text-capitalize profile-form__button"
						@click="validateAndSubmit"
						:loading="loading"
						>Change
					</v-btn>
				</v-col>
			</v-row>
		</v-card>
	</v-form>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import ImageInput from '@/components/ImageInput'
import Form from '@/mixins/Form'

import { ruleRequired, ruleNotEmpty } from '@/utils/rules'

export default {
	name: 'ProfileForm',
	mixins: [Form],
	components: {
		ImageInput,
	},
	data() {
		return {
			fields: {
				id: 0,
				name: '',
				bio: '',
				avatar: '',
			},
			avatar: {
				imageUrl: '',
				imageFile: undefined,
			},
			loader: {
				class: 'mb-6',
				boilerplate: true,
				elevation: 2,
			},
			saving: false,
			saved: false,
			avatarSrc: undefined,
		}
	},
	watch: {
		profile: {
			handler(profile) {
				this.avatarSrc = profile?.avatar
				this.fields = { ...this.fields, ...profile }
			},
		},
		avatar: {
			handler(value) {
				this.avatarSrc = value.imageURL
				this.fields.avatar = value.imageFile
			},
			deep: true,
		},
	},
	computed: {
		...mapGetters('auth', ['profile']),
		rules() {
			return {
				name: [
					(v) => ruleRequired(v) || 'Field is required.',
					(v) => ruleNotEmpty(v) || 'Name cannot consist of one space',
				],
			}
		},
		userProfile() {
			return this.profile
		},
	},
	methods: {
		...mapActions('auth', ['updateProfile']),
		removeAvatar() {
			this.avatarSrc = ''
			this.fields.avatar = ''
		},
		submitForm() {
			this.field_errors = {}
			const data = new FormData()

			data.append('name', this.fields.name)
			data.append('bio', this.fields.bio)
			data.append('avatar', this.fields.avatar)

			data.append('_method', 'PUT')

			return this.updateProfile({ userId: this.fields.id, userData: data })
		},
	},
	created() {
		this.avatarSrc = this.userProfile?.avatar

		this.fields = {
			...this.fields,
			...this.profile,
		}
	},
}
</script>

<style lang="scss">
.profile-form {
	.v-input__slot::before {
		border-style: none !important;
	}

	.v-input {
		font-weight: 500;
		font-size: 22px;
		color: #cbcbcb;
		opacity: 1;
	}

	input::placeholder {
		font-weight: 600;
		font-size: 22px;
		color: #cbcbcb !important;
		opacity: 1;
	}

	&__title {
		font-style: normal;
		font-weight: 700;
		font-size: 20px;
		letter-spacing: 0.1px;
	}

	&__description {
		font: 500 14px/1.45 'Montserrat';
		color: #cbcbcb;
	}

	&__avatar {
		width: 70px;
		height: 70px;
	}

	&__image-input {
		border-radius: 25px;
		cursor: pointer;
	}

	&__button {
		height: 30px !important;
		width: 108px !important;
		border: 1px solid #4d536e;
		font-style: normal !important;
		font-weight: 600;
		font-size: 15px;
		line-height: 18px;
		background-color: white !important;
		box-shadow: none;
		text-align: center;
		letter-spacing: -0.28px;

		&:hover {
			color: #fff !important;
			background: #4d536e !important;
		}
	}

	.image-close-button {
		&:after {
			content: none;
			opacity: 0;
		}
	}

	&__divider {
		border-color: #000 !important;
		mix-blend-mode: normal;
		opacity: 0.1;
	}
}
</style>
